import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, Button, IconButton, TextField, Menu, MenuItem,
  ListItemIcon, ListItemText, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import TimelineIcon from '@material-ui/icons/Timeline';
import PlaceIcon from '@material-ui/icons/Place';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ImportIcon from '@material-ui/icons/CallSplit';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import NearMeIcon from '@material-ui/icons/NearMe';
import {
  AREA_CATEGORIES, AREA_TYPE_COLORS, AREA_TYPE_LABELS, CATEGORY_COLORS,
  getAreaTypeColor, getAreaTypeLabel, resolveAreaType, isExclusionArea,
  SPRAY_TYPES, SPRAY_TYPE_LABELS, SPRAY_TYPE_COLORS, getSprayTypeForArea,
} from '../../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  actionRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  actionBtn: {
    flex: 1,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
  },
  searchBox: {
    marginBottom: 12,
    '& .MuiOutlinedInput-root': { fontSize: 13 },
    '& .MuiOutlinedInput-input': { padding: '8px 8px' },
  },
  legacyBanner: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderLeft: '4px solid #ff9800',
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  legacyTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#e65100',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  legacyHint: {
    fontSize: 11,
    color: '#b45309',
    marginBottom: 10,
    lineHeight: 1.4,
  },
  legacyBtn: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
  },
  categorySection: {
    marginBottom: 20,
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    padding: '4px 0',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { background: '#fafafa', borderRadius: 4 },
  },
  categorySwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
  },
  categoryLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  categoryMeta: {
    fontSize: 11,
    color: '#999',
    marginLeft: 4,
  },
  areaCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 8,
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#bdbdbd',
      background: '#fafafa',
    },
  },
  areaDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  areaInfo: {
    flex: 1,
    minWidth: 0,
  },
  areaName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  areaMeta: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  totalsFooter: {
    padding: 14,
    background: '#f5f5f5',
    borderRadius: 8,
    marginTop: 8,
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalsLabel: {
    fontSize: 13,
    color: '#666',
  },
  totalsValue: {
    fontSize: 16,
    fontWeight: 500,
    color: '#333',
  },
  totalsMeta: {
    fontSize: 11,
    color: '#999',
    marginTop: 4,
  },
  empty: {
    padding: 32,
    textAlign: 'center',
    color: '#999',
    fontSize: 13,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    background: '#EEF2FF',
    borderRadius: 6,
    marginBottom: 6,
    cursor: 'pointer',
    '&:hover': { background: '#E0E7FF' },
  },
  groupLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#4338CA',
    flex: 1,
  },
  groupMeta: {
    fontSize: 11,
    color: '#6366F1',
  },
  groupedCard: {
    marginLeft: 12,
    borderLeft: '2px solid #C7D2FE',
  },
  typeSummary: {
    padding: 12,
    background: '#F9FAFB',
    borderRadius: 8,
    marginBottom: 16,
    border: '1px solid #E5E7EB',
  },
  typeSummaryTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 8,
  },
  typeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '3px 0',
    fontSize: 12,
  },
  typeDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  typeLabel: {
    flex: 1,
    color: '#374151',
  },
  typeValue: {
    fontWeight: 600,
    color: '#111827',
    textAlign: 'right',
  },
  typeSF: {
    fontSize: 11,
    color: '#9CA3AF',
    textAlign: 'right',
    minWidth: 70,
  },
  sectionDivider: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginTop: 16,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottom: '1px solid #E5E7EB',
  },
}));

const formatSF = (num) => {
  const n = parseFloat(num) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatAcres = (num) => {
  const n = parseFloat(num) || 0;
  return `${(n / 43560).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ac`;
};

const formatReadableArea = (num) => `${formatAcres(num)} (${formatSF(num)} SF)`;

// Smart: show acres when large, SF when small — single unit, not both
const formatSmartArea = (num) => {
  const n = parseFloat(num) || 0;
  if (n >= 25000) return formatAcres(n);
  return `${formatSF(n)} SF`;
};

// Derived category for the V2 grouped view. Takes each area and picks the
// display category (Turf / Desert+Gravel / Beds / Hardscape / ...)
const getDisplayCategory = (areaType) => {
  const resolved = resolveAreaType(areaType);
  for (const [catKey, cat] of Object.entries(AREA_CATEGORIES)) {
    if (cat.types.includes(resolved)) return { key: catKey, label: cat.label };
  }
  return { key: 'OTHER', label: 'Other' };
};

// Fixed color per category for the sidebar header swatch
const getCategoryColor = (catKey) => CATEGORY_COLORS[catKey] || '#999';

const AreasMode = ({
  areas = [],
  onDrawArea,
  snapping,
  onToggleSnapping,
  onEditArea,
  jobsite,
  hasLegacyAreas,
  legacyAreaCount = 0,
  onImportLegacyAreas,
  legacyImportCandidates = [],
  onIgnoreLegacyAreas,
  ignoredLegacyAreaCount = 0,
  onRestoreIgnoredLegacyAreas,
  onZoomToArea,
  saving,
  onBulkEdit,
  hiddenAreaIds = new Set(),
  onToggleVisibility,
}) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [drawMenuAnchor, setDrawMenuAnchor] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [importName, setImportName] = useState('Imported Areas');
  const [selectedImportIds, setSelectedImportIds] = useState(new Set());

  // Filter out exclusion areas from the main list — they only appear nested in their parent's editor
  const parentAreas = useMemo(() => areas.filter(a => !isExclusionArea(a)), [areas]);

  // Build a map of parent area -> total exclusion SF for net area display
  const exclusionSFByParent = useMemo(() => {
    const map = {};
    areas.forEach(a => {
      if (!isExclusionArea(a)) return;
      const pid = typeof a.parentAreaId === 'object' ? a.parentAreaId.toString() : a.parentAreaId;
      map[pid] = (map[pid] || 0) + (parseFloat(a.area) || 0);
    });
    return map;
  }, [areas]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return parentAreas;
    return parentAreas.filter(a => {
      const name = (a.name || '').toLowerCase();
      const type = getAreaTypeLabel(a.areaType).toLowerCase();
      return name.includes(q) || type.includes(q);
    });
  }, [parentAreas, searchQuery]);

  // Group areas by display category
  const grouped = useMemo(() => {
    const groups = {};
    filtered.filter(area => !area.groupId).forEach(area => {
      const { key, label } = getDisplayCategory(area.areaType);
      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          color: getCategoryColor(key),
          areas: [],
          totalSF: 0,
        };
      }
      const areaId = area._id || area.id;
      const excSF = exclusionSFByParent[areaId] || 0;
      const netSF = (parseFloat(area.area) || 0) - excSF;
      groups[key].areas.push(area);
      groups[key].totalSF += netSF;
    });
    return Object.values(groups);
  }, [filtered, exclusionSFByParent]);

  // Aggregate by spray type — the key view for herbicide mix calculations
  const spraySummary = useMemo(() => {
    const map = {};
    filtered.forEach(area => {
      const sprayType = getSprayTypeForArea(area.areaType);
      if (sprayType === SPRAY_TYPES.NONE) return;
      const areaId = area._id || area.id;
      const excSF = exclusionSFByParent[areaId] || 0;
      const netSF = (parseFloat(area.area) || 0) - excSF;
      if (!map[sprayType]) map[sprayType] = { type: sprayType, label: SPRAY_TYPE_LABELS[sprayType], color: SPRAY_TYPE_COLORS[sprayType], totalSF: 0, count: 0 };
      map[sprayType].totalSF += netSF;
      map[sprayType].count++;
    });
    return Object.values(map).sort((a, b) => b.totalSF - a.totalSF);
  }, [filtered, exclusionSFByParent]);

  // Aggregate by area type — for the summary near totals
  const typeSummary = useMemo(() => {
    const map = {};
    filtered.forEach(area => {
      const resolved = resolveAreaType(area.areaType);
      const label = getAreaTypeLabel(resolved);
      const color = getAreaTypeColor(resolved);
      const areaId = area._id || area.id;
      const excSF = exclusionSFByParent[areaId] || 0;
      const netSF = (parseFloat(area.area) || 0) - excSF;
      if (!map[resolved]) map[resolved] = { type: resolved, label, color, totalSF: 0, count: 0 };
      map[resolved].totalSF += netSF;
      map[resolved].count++;
    });
    return Object.values(map).sort((a, b) => b.totalSF - a.totalSF);
  }, [filtered, exclusionSFByParent]);

  // Separate areas into groups and ungrouped for the flat list
  const { areaGroups } = useMemo(() => {
    const gMap = {};
    filtered.forEach(area => {
      if (area.groupId) {
        if (!gMap[area.groupId]) gMap[area.groupId] = { id: area.groupId, name: area.groupName || 'Group', areas: [] };
        gMap[area.groupId].areas.push(area);
      }
    });
    return { areaGroups: Object.values(gMap) };
  }, [filtered]);

  const importCandidates = legacyImportCandidates?.length
    ? legacyImportCandidates
    : (hasLegacyAreas ? [] : []);

  const selectedImportAreas = importCandidates.filter(a => selectedImportIds.has(a.importId));

  const openImportDialog = () => {
    const nextSelected = new Set(importCandidates.map(a => a.importId));
    setSelectedImportIds(nextSelected);
    setImportName('Imported Areas');
    setImportOpen(true);
  };

  const toggleImportId = (importId) => {
    setSelectedImportIds(prev => {
      const next = new Set(prev);
      if (next.has(importId)) next.delete(importId);
      else next.add(importId);
      return next;
    });
  };

  const toggleAllImport = () => {
    setSelectedImportIds(prev => {
      if (prev.size === importCandidates.length) return new Set();
      return new Set(importCandidates.map(a => a.importId));
    });
  };

  // Totals
  const totals = useMemo(() => {
    let totalSF = 0;
    let totalLF = 0;
    filtered.forEach(a => {
      const areaId = a._id || a.id;
      const excSF = exclusionSFByParent[areaId] || 0;
      totalSF += (parseFloat(a.area) || 0) - excSF;
      if (a.geometryType === 'polyline') {
        totalLF += parseFloat(a.length) || 0;
      } else {
        totalLF += parseFloat(a.perimeter) || 0;
      }
    });
    return {
      totalSF,
      totalLF,
      totalAcres: totalSF / 43560,
    };
  }, [filtered, exclusionSFByParent]);

  const handleDrawClick = (e) => {
    setDrawMenuAnchor(e.currentTarget);
  };

  const handleDrawTool = (tool) => {
    setDrawMenuAnchor(null);
    if (onDrawArea) onDrawArea(tool);
  };

  const renderCard = (area, extraClass) => {
    const areaId = area._id || area.id;
    const color = getAreaTypeColor(area.areaType);
    const typeLabel = getAreaTypeLabel(area.areaType);
    const hasPreEmergent = (area.servicesRequired || []).includes('pre_emergent');
    const excSF = exclusionSFByParent[areaId] || 0;
    const grossSF = parseFloat(area.area) || 0;
    const displaySF = grossSF - excSF;
    const isHidden = hiddenAreaIds.has(areaId);
    return (
      <div
        key={areaId}
        className={`${classes.areaCard} ${extraClass || ''}`}
        onClick={() => onEditArea && onEditArea(areaId)}
        style={isHidden ? { opacity: 0.45 } : undefined}
      >
        <div className={classes.areaDot} style={{ background: color }} />
        <div className={classes.areaInfo}>
          <div className={classes.areaName}>{area.name || typeLabel}</div>
          <div className={classes.areaMeta}>{typeLabel}</div>
          <div className={classes.areaMeta}>
            {excSF > 0 ? (
              <>{formatSmartArea(displaySF)} net · {formatSF(grossSF)} SF gross</>
            ) : grossSF >= 25000 ? (
              <>{formatAcres(grossSF)} <span style={{ opacity: 0.6 }}>({formatSF(grossSF)} SF)</span></>
            ) : (
              <>{formatSF(grossSF)} SF</>
            )}
            {area.geometryType === 'polyline' && area.length ? ` · ${formatSF(area.length)} LF` : ''}
          </div>
        </div>
        {hasPreEmergent && (
          <span style={{
            fontSize: 10, padding: '2px 6px',
            background: '#FFFBEB', color: '#92400E', borderRadius: 4,
          }}>
            Pre-Em
          </span>
        )}
        {onToggleVisibility && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggleVisibility(areaId); }}
            style={{ padding: 4 }}
            title={hiddenAreaIds.has(areaId) ? 'Show on map' : 'Hide on map'}
          >
            {hiddenAreaIds.has(areaId)
              ? <VisibilityOffIcon style={{ fontSize: 16, color: '#ccc' }} />
              : <VisibilityIcon style={{ fontSize: 16, color: '#999' }} />}
          </IconButton>
        )}
        {onZoomToArea && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onZoomToArea(areaId); }}
            style={{ padding: 4 }}
          >
            <CenterFocusStrongIcon style={{ fontSize: 16, color: '#999' }} />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <Box>
      {/* Action row */}
      <div className={classes.actionRow}>
        <Button
          variant="outlined"
          className={classes.actionBtn}
          startIcon={<AddIcon />}
          onClick={handleDrawClick}
          disabled={!jobsite?._id}
          data-tour="site-takeoff-draw-area"
          style={{
            background: '#EEF2FF',
            color: '#4F46E5',
            borderColor: '#C7D2FE',
          }}
        >
          Draw area
        </Button>
        <Menu
          anchorEl={drawMenuAnchor}
          open={Boolean(drawMenuAnchor)}
          onClose={() => setDrawMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleDrawTool('polygon')}>
            <ListItemIcon><CropSquareIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Polygon" secondary="Beds, turf, hardscape" />
          </MenuItem>
          <MenuItem onClick={() => handleDrawTool('polyline')}>
            <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Line / Trail" secondary="Sidewalks, edging, trails" />
          </MenuItem>
          <MenuItem onClick={() => handleDrawTool('ellipse')}>
            <ListItemIcon><RadioButtonUncheckedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Circle / Ellipse" secondary="Round features" />
          </MenuItem>
          <MenuItem onClick={() => handleDrawTool('point')}>
            <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Point Marker" secondary="Trees, fixtures" />
          </MenuItem>
        </Menu>

        {/* Snap toggle */}
        {onToggleSnapping && (
          <Tooltip title={snapping ? 'Snapping on — edges snap to nearby areas' : 'Snapping off — free drawing'}>
            <IconButton
              size="small"
              onClick={onToggleSnapping}
              style={{
                padding: 6,
                background: snapping ? '#E8F5E9' : '#f5f5f5',
                borderRadius: 6,
              }}
            >
              <NearMeIcon style={{ fontSize: 16, color: snapping ? '#2e7d32' : '#999' }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Bulk edit button */}
      {parentAreas.length > 1 && onBulkEdit && (
        <Button
          size="small"
          fullWidth
          onClick={onBulkEdit}
          style={{
            textTransform: 'none',
            fontSize: 12,
            color: '#4F46E5',
            marginBottom: 12,
            background: '#F5F3FF',
            borderRadius: 6,
          }}
        >
          Bulk edit {parentAreas.length} areas
        </Button>
      )}

      {/* Legacy area import banner — only when pre-Takeoff areas exist and no takeoff yet */}
      {hasLegacyAreas && legacyAreaCount > 0 && (
        <div className={classes.legacyBanner}>
          <div className={classes.legacyTitle}>
            <CloudDownloadIcon style={{ fontSize: 16 }} />
            Legacy areas detected
          </div>
          <div className={classes.legacyHint}>
            This jobsite has {legacyAreaCount} {legacyAreaCount === 1 ? 'area' : 'areas'} from
            the old pre-Takeoff system. Import them to create a new takeoff you can edit here.
          </div>
          <Button
            variant="contained"
            size="small"
            fullWidth
            className={classes.legacyBtn}
            startIcon={<ImportIcon style={{ fontSize: 16 }} />}
            onClick={openImportDialog}
            disabled={saving || importCandidates.length === 0}
            style={{
              background: '#ff9800',
              color: '#fff',
            }}
          >
            {saving
              ? 'Importing...'
              : importCandidates.length > 0
                ? `Review ${legacyAreaCount} ${legacyAreaCount === 1 ? 'area' : 'areas'}`
                : 'No importable areas'}
          </Button>
          {ignoredLegacyAreaCount > 0 && onRestoreIgnoredLegacyAreas && (
            <Button
              size="small"
              fullWidth
              className={classes.legacyBtn}
              onClick={onRestoreIgnoredLegacyAreas}
              style={{ marginTop: 6 }}
            >
              Restore {ignoredLegacyAreaCount} ignored
            </Button>
          )}
        </div>
      )}

      {/* Search */}
      {parentAreas.length >= 5 && (
        <TextField
          className={classes.searchBox}
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Search areas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon style={{ fontSize: 16, color: '#999', marginRight: 4 }} />,
          }}
        />
      )}

      {/* Empty state */}
      {parentAreas.length === 0 && (
        <div className={classes.empty}>
          {jobsite?._id
            ? 'No areas yet. Click "Draw area" to start mapping this property.'
            : 'Select a jobsite to begin.'}
        </div>
      )}

      {/* Spray type summary — key for herbicide mix calculations */}
      {spraySummary.length > 0 && (
        <div className={classes.typeSummary}>
          <div className={classes.typeSummaryTitle}>Spray Coverage</div>
          {spraySummary.map(t => (
            <div key={t.type} className={classes.typeRow}>
              <div className={classes.typeDot} style={{ background: t.color }} />
              <span className={classes.typeLabel}>{t.label} ({t.count})</span>
              <span className={classes.typeValue}>
                {t.totalSF >= 25000 ? formatAcres(t.totalSF) : `${formatSF(t.totalSF)} SF`}
              </span>
              {t.totalSF >= 25000 && (
                <span className={classes.typeSF}>{formatSF(t.totalSF)} SF</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Logical area groups */}
      {areaGroups.length > 0 && (
        <div className={classes.categorySection}>
          <div className={classes.sectionDivider}>Groups</div>
          {areaGroups.map(group => {
            const isCollapsed = !!collapsedGroups[`group_${group.id}`];
            const totalSF = group.areas.reduce((sum, area) => {
              const areaId = area._id || area.id;
              const excSF = exclusionSFByParent[areaId] || 0;
              return sum + ((parseFloat(area.area) || 0) - excSF);
            }, 0);
            return (
              <div key={group.id} style={{ marginBottom: 8 }}>
                <div
                  className={classes.groupHeader}
                  onClick={() => setCollapsedGroups(prev => ({ ...prev, [`group_${group.id}`]: !prev[`group_${group.id}`] }))}
                >
                  <span style={{ fontSize: 10, color: '#4338CA', width: 10 }}>
                    {isCollapsed ? '▸' : '▾'}
                  </span>
                  <span className={classes.groupLabel}>{group.name}</span>
                  <span className={classes.groupMeta}>{group.areas.length} · {formatSF(totalSF)} SF</span>
                  {onUngroupAll && (
                    <Button
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onUngroupAll(group.id); }}
                      style={{ textTransform: 'none', fontSize: 11, minWidth: 0, padding: '2px 6px' }}
                    >
                      Ungroup
                    </Button>
                  )}
                </div>
                {!isCollapsed && group.areas.map(a => renderCard(a, classes.groupedCard))}
              </div>
            );
          })}
        </div>
      )}

      {/* Category groups — collapsible with group-level visibility */}
      {grouped.map(group => {
        const isCollapsed = !!collapsedGroups[`cat_${group.key}`];
        const groupAreaIds = group.areas.map(a => a._id || a.id);
        const allHidden = groupAreaIds.length > 0 && groupAreaIds.every(id => hiddenAreaIds.has(id));
        const someHidden = groupAreaIds.some(id => hiddenAreaIds.has(id));

        const handleToggleGroupVisibility = (e) => {
          e.stopPropagation();
          if (onToggleVisibility) {
            // If all are hidden, show all. Otherwise hide all.
            groupAreaIds.forEach(id => {
              const isHidden = hiddenAreaIds.has(id);
              if (allHidden && isHidden) onToggleVisibility(id);
              else if (!allHidden && !isHidden) onToggleVisibility(id);
            });
          }
        };

        return (
          <div key={group.key} className={classes.categorySection}>
            <div
              className={classes.categoryHeader}
              onClick={() => setCollapsedGroups(prev => ({ ...prev, [`cat_${group.key}`]: !prev[`cat_${group.key}`] }))}
            >
              <span style={{ fontSize: 10, color: '#666', width: 10 }}>
                {isCollapsed ? '▸' : '▾'}
              </span>
              <div
                className={classes.categorySwatch}
                style={{ background: group.color }}
              />
              <span className={classes.categoryLabel} style={{ flex: 1 }}>{group.label}</span>
              <span className={classes.categoryMeta}>
                {group.areas.length} · {formatSF(group.totalSF)} SF
              </span>
              {onToggleVisibility && (
                <IconButton
                  size="small"
                  onClick={handleToggleGroupVisibility}
                  style={{ padding: 2, marginLeft: 2 }}
                  title={allHidden ? 'Show all in group' : 'Hide all in group'}
                >
                  {allHidden
                    ? <VisibilityOffIcon style={{ fontSize: 14, color: '#ccc' }} />
                    : <VisibilityIcon style={{ fontSize: 14, color: someHidden ? '#bbb' : '#999' }} />}
                </IconButton>
              )}
            </div>

            {!isCollapsed && group.areas.map(a => renderCard(a))}
          </div>
        );
      })}

      {/* Area type breakdown */}
      {typeSummary.length > 0 && (
        <div className={classes.typeSummary} style={{ marginTop: 16 }}>
          <div className={classes.typeSummaryTitle}>Area by Type</div>
          {typeSummary.map(t => (
            <div key={t.type} className={classes.typeRow}>
              <div className={classes.typeDot} style={{ background: t.color }} />
              <span className={classes.typeLabel}>{t.label} ({t.count})</span>
              <span className={classes.typeValue}>
                {t.totalSF >= 25000 ? formatAcres(t.totalSF) : `${formatSF(t.totalSF)} SF`}
              </span>
              {t.totalSF >= 25000 && (
                <span className={classes.typeSF}>{formatSF(t.totalSF)} SF</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Totals footer */}
      {parentAreas.length > 0 && (
        <div className={classes.totalsFooter}>
          <div className={classes.totalsRow}>
            <span className={classes.totalsLabel}>Total landscape</span>
            <span className={classes.totalsValue}>{formatAcres(totals.totalSF)}</span>
          </div>
          <div className={classes.totalsMeta}>
            {formatSF(totals.totalSF)} SF · {formatSF(totals.totalLF)} LF perimeter
          </div>
        </div>
      )}

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import areas</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Takeoff name"
            value={importName}
            onChange={(e) => setImportName(e.target.value)}
            style={{ marginBottom: 12 }}
            autoFocus
            onFocus={(e) => e.target.select()}
          />
          <Box display="flex" alignItems="center" mb={1}>
            <Checkbox
              size="small"
              checked={importCandidates.length > 0 && selectedImportIds.size === importCandidates.length}
              indeterminate={selectedImportIds.size > 0 && selectedImportIds.size < importCandidates.length}
              onChange={toggleAllImport}
            />
            <Typography variant="caption">
              {selectedImportIds.size} of {importCandidates.length} selected
            </Typography>
          </Box>
          <Box maxHeight={320} overflow="auto">
            {importCandidates.map(area => {
              const label = area.name || getAreaTypeLabel(area.areaType);
              return (
                <Box
                  key={area.importId}
                  display="flex"
                  alignItems="center"
                  gridGap={8}
                  border="1px solid #e0e0e0"
                  borderRadius={6}
                  padding="6px 8px"
                  mb={0.5}
                >
                  <Checkbox
                    size="small"
                    checked={selectedImportIds.has(area.importId)}
                    onChange={() => toggleImportId(area.importId)}
                    style={{ padding: 2 }}
                  />
                  <span className={classes.areaDot} style={{ background: getAreaTypeColor(area.areaType) }} />
                  <Box flex={1} minWidth={0}>
                    <Typography style={{ fontSize: 13, fontWeight: 500 }} noWrap>{label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getAreaTypeLabel(area.areaType)} · {formatSF(area.area)} SF
                    </Typography>
                  </Box>
                  {onIgnoreLegacyAreas && (
                    <Button
                      size="small"
                      onClick={() => {
                        onIgnoreLegacyAreas([area.importId]);
                        setSelectedImportIds(prev => {
                          const next = new Set(prev);
                          next.delete(area.importId);
                          return next;
                        });
                      }}
                      style={{ textTransform: 'none', fontSize: 11 }}
                    >
                      Ignore
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          {onIgnoreLegacyAreas && selectedImportIds.size > 0 && (
            <Button
              onClick={() => {
                onIgnoreLegacyAreas(Array.from(selectedImportIds));
                setImportOpen(false);
              }}
            >
              Ignore selected
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={saving || selectedImportAreas.length === 0 || !importName.trim()}
            onClick={async () => {
              await onImportLegacyAreas(selectedImportAreas, importName.trim());
              setImportOpen(false);
            }}
          >
            Import selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AreasMode;
